import ConfirmationDialog from 'components/Common/ConfirmationDialog';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Dialog, Divider, Link, Stack, Typography,
} from '@mui/material';
import {
  HButton, HDialogActions, HDialogContent, HDialogTitle, HSpinner,
} from 'BaseComponents';
import { LoadingSkeleton } from 'components/CleanRooms/CurrentQuestions/QuestionPermissionsOverridesModal/LoadingSkeleton';
import { QuestionPermissionsItem } from 'components/CleanRooms/CurrentQuestions/QuestionPermissionsOverridesModal/QuestionPermissionsItem';
import { cleanRoom } from 'utils/spaUrls';
import {
  cleanRoomQuestionPermissions,
  crqPermissionsAccessLevels,
} from 'components/CleanRooms/constants';
import { showAlert } from 'redux/actions/AlertActions';
import { useDispatch, useSelector } from 'react-redux';
import { useFetchCleanRoomDetails } from 'hooks/cleanRoomAPIs';
import {
  useFetchCrQuestionPermissions, useUpdateCrQuestionPermissions,
} from 'components/CleanRooms/CurrentQuestions/service/CrQuestionPermissionsAPI';


export const QuestionPermissionsOverridesModal = ({
  open, onCancel, crId, questionID, questionName,
}) => {
  const dispatch = useDispatch();
  const orgId = useSelector(state => state.session.user.primaryOrganization.organization.ID);
  const [permissions, setPermissions] = useState([]);
  const [validation, setValidation] = useState(true);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const {
    isFetching: isFetchingCrDetails,
    isError: isErrorCrDetails,
    data: crDetails,
  } = useFetchCleanRoomDetails(orgId, crId);

  const orgList = useMemo(() => {
    if (!crDetails?.cleanRoom) return [];
    const owner = {
      organizationID: crDetails?.cleanRoom?.organizationID,
      name: crDetails?.cleanRoom?.organizationName,
    };
    const partners = crDetails?.partners?.map(partner => ({
      organizationID: partner.organizationID,
      name: partner.name,
    })) || [];
    return [owner, ...partners];
  }, [crDetails]);

  const {
    data: questionPermissions,
    isFetching: isFetchingQuestionPermissions,
    isError: isErrorQuestionPermissions,
  } = useFetchCrQuestionPermissions(orgId, crId, questionID);

  const updatePermissions = useUpdateCrQuestionPermissions();

  useEffect(() => {
    if (updatePermissions.isSuccess) {
      onCancel();
      dispatch(showAlert({ message: 'Clean Room Question permissions have been updated.', type: 'success' }));
      updatePermissions.reset();
    }

    if (updatePermissions.isError) {
      dispatch(showAlert({ message: updatePermissions.data.response.data.message, type: 'error' }));
    }
  }, [dispatch, onCancel, updatePermissions]);

  useEffect(() => {
    if (!questionPermissions) return;
    const perms = Object.values(cleanRoomQuestionPermissions).map(p => {
      const questionPermission = questionPermissions.find(qp => qp.permission === p.name);
      if (questionPermission) {
        return questionPermission;
      }
      else {
        return { permission: p.name, access: crqPermissionsAccessLevels.QUESTION_OWNER_ONLY };
      }
    });
    setPermissions(perms);
  }, [questionID, questionPermissions]);

  const save = () => {
    const payload = {
      cleanRoomQuestionPermissions: permissions.map(p => ({
        permission: p.permission,
        access: p.access,
        partners: (p?.partners ?? []).map(partner => partner.ID),
      })),
    };

    setOpenDeleteModal(false);

    updatePermissions.mutate({
      orgId,
      crId,
      crqId: questionID,
      payload,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      fullWidth
      maxWidth='sm'
      aria-labelledby='manage-question-permissions-dialog'
      aria-describedby='manage-question-permissions-dialog'
    >
      <HDialogTitle onClose={onCancel}>
        <Stack gap={1}>
          <Typography variant='h3'>Question Permissions</Typography>
          <Typography variant='body2'>{questionName}</Typography>
        </Stack>
      </HDialogTitle>
      <HDialogContent>
        <Stack gap={1}>
          <Typography variant='body2'>
            Enable or disable how partners interact with questions.
          </Typography>
          <Typography variant='body2' display='inline-flex' gap={0.5}>
            Set Clean Room level permissions in
            <Typography variant='body2'>
              <Link
                href={cleanRoom.cleanRoomPermissions(crId)}
                target='_self'
                rel='noreferrer'
              >
                Parameters and Permissions
              </Link>
              .
            </Typography>
          </Typography>

          {isFetchingQuestionPermissions && isFetchingCrDetails ? <LoadingSkeleton /> : (
            <Stack
              my={2}
              divider={<Divider />}
              spacing={2}
            >
              {Object.values(cleanRoomQuestionPermissions).map(p => (
                <QuestionPermissionsItem
                  key={p.name}
                  permission={p}
                  permissions={permissions}
                  setPermissions={setPermissions}
                  orgList={orgList}
                  setValidation={setValidation}
                  exdenedPartnerPermissions={[
                    cleanRoomQuestionPermissions.VIEW_RESULTS.name,
                    cleanRoomQuestionPermissions.CLONE.name,
                    cleanRoomQuestionPermissions.EDIT.name,
                    cleanRoomQuestionPermissions.VIEW_CODE.name,
                    cleanRoomQuestionPermissions.RUN.name,
                  ]}
                />
              ))}

              {(isErrorQuestionPermissions || isErrorCrDetails) && (
                <Alert severity='error'>Something went wrong.</Alert>
              )}
            </Stack>
          )}
        </Stack>
      </HDialogContent>
      <HDialogActions>
        {onCancel ? (
          <HButton variant='outlined' onClick={onCancel} size='small' data-testid='cancel-button'>
            Cancel
          </HButton>
        ) : null}
        <HButton
          variant='contained'
          color='primary'
          size='small'
          onClick={() => setOpenDeleteModal(true)}
          endIcon={updatePermissions.isLoading ? <HSpinner isButton /> : null}
          disabled={updatePermissions.isLoading || !validation}
          data-testid='save-button'
        >
          Save
        </HButton>
      </HDialogActions>

      <ConfirmationDialog
        open={openDeleteModal}
        title='Are you sure?'
        message='Question permissions will override clean room permissions.'
        confirmActionLabel='Yes'
        onConfirm={save}
        onCancel={() => setOpenDeleteModal(false)}
      />
    </Dialog>
  );
};
